import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid2,
  IconButton,
  Paper,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

// ---------------------------------------------------------------------------
// GQL
// ---------------------------------------------------------------------------

const GET_PARSED_DECISION = gql`
  query GetParsedDecision($parsedDecisionId: ID!) {
    parsedDecision(parsedDecisionId: $parsedDecisionId) {
      parsedDecisionId
      reviewStatus
      confidenceScore
      requiresManualReview
      modelUsed
      createdDateTime
      parsedData {
        decisionType
        decisionDate
        expirationDate
        approvalAmount
        termLengthMonths
        interestRate
        monthlyPayment
        conditions
        declineReasons
        stipulations
        lenderFees {
          feeName
          amount
          canBeFinanced
          description
        }
        applicantName
        businessName
        firstPaymentDueDate
        advancePaymentsCount
      }
    }
  }
`;

const UPDATE_PARSED_DECISION = gql`
  mutation UpdateParsedDecision($input: UpdateParsedDecisionInput!) {
    updateParsedDecision(input: $input) {
      parsedDecisionId
      reviewStatus
      parsedData {
        approvalAmount
        interestRate
        termLengthMonths
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toNumber(v: string): number | null {
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
}

function toInt(v: string): number | null {
  const n = parseInt(v, 10);
  return isNaN(n) ? null : n;
}

function toStringArray(v: string): string[] {
  return v
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ParsedDecisionEdit: React.FC = () => {
  const { parsedDecisionId } = useParams<{ parsedDecisionId: string }>();
  const history = useHistory();

  const { data, loading, error } = useQuery(GET_PARSED_DECISION, {
    variables: { parsedDecisionId },
    skip: !parsedDecisionId,
  });

  const [updateParsedDecision, { loading: saving }] = useMutation(UPDATE_PARSED_DECISION);

  // Form state mirrors CreditDecisionData fields
  const [form, setForm] = useState({
    decisionType: '',
    decisionDate: '',
    expirationDate: '',
    approvalAmount: '',
    termLengthMonths: '',
    interestRate: '',
    monthlyPayment: '',
    applicantName: '',
    businessName: '',
    firstPaymentDueDate: '',
    advancePaymentsCount: '',
    // Multi-line text areas (newline-delimited)
    conditions: '',
    declineReasons: '',
    stipulations: '',
  });

  useEffect(() => {
    const pd = data?.parsedDecision?.parsedData;
    if (!pd) return;
    setForm({
      decisionType: pd.decisionType ?? '',
      decisionDate: pd.decisionDate ?? '',
      expirationDate: pd.expirationDate ?? '',
      approvalAmount: pd.approvalAmount != null ? String(pd.approvalAmount) : '',
      termLengthMonths: pd.termLengthMonths != null ? String(pd.termLengthMonths) : '',
      interestRate: pd.interestRate != null ? String(pd.interestRate) : '',
      monthlyPayment: pd.monthlyPayment != null ? String(pd.monthlyPayment) : '',
      applicantName: pd.applicantName ?? '',
      businessName: pd.businessName ?? '',
      firstPaymentDueDate: pd.firstPaymentDueDate ?? '',
      advancePaymentsCount: pd.advancePaymentsCount != null ? String(pd.advancePaymentsCount) : '',
      conditions: (pd.conditions ?? []).join('\n'),
      declineReasons: (pd.declineReasons ?? []).join('\n'),
      stipulations: (pd.stipulations ?? []).join('\n'),
    });
  }, [data]);

  const handleChange = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    const parsedData: Record<string, any> = {
      decisionType: form.decisionType || null,
      decisionDate: form.decisionDate || null,
      expirationDate: form.expirationDate || null,
      approvalAmount: toNumber(form.approvalAmount),
      termLengthMonths: toInt(form.termLengthMonths),
      interestRate: toNumber(form.interestRate),
      monthlyPayment: toNumber(form.monthlyPayment),
      applicantName: form.applicantName || null,
      businessName: form.businessName || null,
      firstPaymentDueDate: form.firstPaymentDueDate || null,
      advancePaymentsCount: toInt(form.advancePaymentsCount),
      conditions: toStringArray(form.conditions),
      declineReasons: toStringArray(form.declineReasons),
      stipulations: toStringArray(form.stipulations),
      // Preserve lenderFees as-is from original parsed data
      lenderFees: data?.parsedDecision?.parsedData?.lenderFees ?? [],
    };

    await updateParsedDecision({ variables: { input: { parsedDecisionId, parsedData } } });
    history.goBack();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data?.parsedDecision) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">Failed to load parsed decision.</Typography>
      </Box>
    );
  }

  const meta = data.parsedDecision;

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <IconButton onClick={() => history.goBack()} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700}>
          Edit Parsed Decision
        </Typography>
        <Chip
          label={meta.reviewStatus?.replace(/_/g, ' ')}
          size="small"
          color={meta.reviewStatus === 'APPROVED' ? 'success' : 'default'}
          sx={{ ml: 1 }}
        />
        {meta.confidenceScore != null && (
          <Chip label={`Confidence: ${(meta.confidenceScore * 100).toFixed(0)}%`} size="small" variant="outlined" />
        )}
      </Box>

      <Paper sx={{ p: 3 }}>
        {/* Decision Overview */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Decision Overview
        </Typography>
        <Grid2 container spacing={2} sx={{ mb: 3 }}>
          <Grid2 size={6}>
            <TextField
              label="Decision Type"
              fullWidth
              size="small"
              value={form.decisionType}
              onChange={handleChange('decisionType')}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              label="Decision Date"
              fullWidth
              size="small"
              value={form.decisionDate}
              onChange={handleChange('decisionDate')}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              label="Expiration Date"
              fullWidth
              size="small"
              value={form.expirationDate}
              onChange={handleChange('expirationDate')}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              label="First Payment Due Date"
              fullWidth
              size="small"
              value={form.firstPaymentDueDate}
              onChange={handleChange('firstPaymentDueDate')}
            />
          </Grid2>
        </Grid2>

        <Divider sx={{ my: 2 }} />

        {/* Financial Terms */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Financial Terms
        </Typography>
        <Grid2 container spacing={2} sx={{ mb: 3 }}>
          <Grid2 size={6}>
            <TextField
              label="Approval Amount ($)"
              fullWidth
              size="small"
              type="number"
              value={form.approvalAmount}
              onChange={handleChange('approvalAmount')}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              label="Interest Rate (%)"
              fullWidth
              size="small"
              type="number"
              value={form.interestRate}
              onChange={handleChange('interestRate')}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              label="Term Length (months)"
              fullWidth
              size="small"
              type="number"
              value={form.termLengthMonths}
              onChange={handleChange('termLengthMonths')}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              label="Monthly Payment ($)"
              fullWidth
              size="small"
              type="number"
              value={form.monthlyPayment}
              onChange={handleChange('monthlyPayment')}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              label="Advance Payments Count"
              fullWidth
              size="small"
              type="number"
              value={form.advancePaymentsCount}
              onChange={handleChange('advancePaymentsCount')}
            />
          </Grid2>
        </Grid2>

        <Divider sx={{ my: 2 }} />

        {/* Applicant */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Applicant
        </Typography>
        <Grid2 container spacing={2} sx={{ mb: 3 }}>
          <Grid2 size={6}>
            <TextField
              label="Applicant Name"
              fullWidth
              size="small"
              value={form.applicantName}
              onChange={handleChange('applicantName')}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              label="Business Name"
              fullWidth
              size="small"
              value={form.businessName}
              onChange={handleChange('businessName')}
            />
          </Grid2>
        </Grid2>

        <Divider sx={{ my: 2 }} />

        {/* Conditions / Stipulations / Decline Reasons */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Conditions &amp; Stipulations
        </Typography>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <TextField
              label="Conditions (one per line)"
              fullWidth
              multiline
              minRows={3}
              size="small"
              value={form.conditions}
              onChange={handleChange('conditions')}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              label="Stipulations (one per line)"
              fullWidth
              multiline
              minRows={3}
              size="small"
              value={form.stipulations}
              onChange={handleChange('stipulations')}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              label="Decline Reasons (one per line)"
              fullWidth
              multiline
              minRows={2}
              size="small"
              value={form.declineReasons}
              onChange={handleChange('declineReasons')}
            />
          </Grid2>
        </Grid2>

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={() => history.goBack()} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            Save Changes
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
